import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Landmark, Globe, Building2, Users, AlertTriangle, RotateCw, TrendingUp, ArrowUpRight, ChevronRight, Network } from "lucide-react";
import { api } from "../lib/api";
import { Flag } from "../components/Flag";
import { StatusBadge } from "../components/Badges";
import { ClubCrest } from "../components/ClubCrest";

const CONTAINER = "mx-auto max-w-[1500px] px-4 sm:px-8 lg:px-12";
const PANEL = "rounded-xl bg-[#0f0f0f] border border-[rgba(212,175,55,0.18)]";
const LEVEL_LABEL = { WORLD: "World Body", CONTINENTAL: "Continental Confederation", NATIONAL: "National Federation" };
const HIERARCHY = { WORLD: "Apex of the WSTF pyramid", CONTINENTAL: "Confederation under the WSTF world body", NATIONAL: "National member under its continental confederation" };
const CHILD_LABEL = { WORLD: "Continental Confederations", CONTINENTAL: "National Federations", NATIONAL: "Member Clubs" };

function StatBox({ label, value, Icon }) {
  return (
    <div className={`${PANEL} p-4`}>
      <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-mono mb-1 flex items-center gap-1">{Icon && <Icon className="w-3 h-3 text-[#E5B842]" />}{label}</div>
      <div className="font-heading text-2xl font-black text-slate-50 tabular-nums">{value}</div>
    </div>
  );
}

function FedMiniCard({ f }) {
  const isWorld = f.level === "WORLD";
  return (
    <Link
      to={`/federation/${f.wsbf_fed_id}`}
      data-testid={`fed-child-${f.wsbf_fed_id}`}
      className="group relative rounded-xl bg-[#0f0f0f] border border-[rgba(212,175,55,0.16)] p-4 hover:border-[#E5B842]/55"
      style={{ transition: "border-color 0.18s ease, transform 0.18s ease" }}
      onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-2px)"; }}
      onMouseLeave={(e) => { e.currentTarget.style.transform = ""; }}
    >
      <div className="flex items-center gap-3">
        <div className="relative shrink-0 w-12 h-12 rounded-xl bg-[#000000] border border-[#E5B842]/30 flex items-center justify-center">
          {isWorld ? <Globe className="w-6 h-6 text-[#E5B842]" /> : <Landmark className="w-6 h-6 text-[#E5B842]" />}
          {f.country && <span className="absolute -bottom-1 -right-1"><Flag country={f.country} width={14} /></span>}
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-mono">{LEVEL_LABEL[f.level]}</div>
          <div className="font-heading text-sm font-bold uppercase tracking-tight text-slate-50 truncate">{f.name}</div>
          <div className="font-mono text-[10px] text-[#E5B842] tracking-widest mt-0.5">{f.wsbf_fed_id}</div>
        </div>
        <StatusBadge status={f.recognition_status} />
      </div>
      {f.country && (
        <div className="flex items-center gap-4 mt-3 pt-3 border-t border-[#2b2512] font-mono text-[11px] text-slate-400">
          <span className="inline-flex items-center gap-1"><Building2 className="w-3 h-3" />{f.clubs_count}</span>
          <span className="inline-flex items-center gap-1"><Users className="w-3 h-3" />{f.players_count}</span>
          <ChevronRight className="w-4 h-4 text-slate-600 group-hover:text-[#E5B842] ml-auto" style={{ transition: "color 0.18s ease" }} />
        </div>
      )}
    </Link>
  );
}

export default function FederationPage() {
  const { fedId } = useParams();
  const [f, setF] = useState(null);
  const [all, setAll] = useState([]);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState(false);

  const load = useCallback(() => {
    setF(null); setNotFound(false); setError(false);
    Promise.all([
      api.get(`/federations/${fedId}`),
      api.get("/federations").then(({ data }) => data.federations).catch(() => []),
    ])
      .then(([res, list]) => { setF(res.data); setAll(list || []); })
      .catch((e) => { if (e?.response?.status === 404) setNotFound(true); else setError(true); });
  }, [fedId]);
  useEffect(() => { load(); }, [load]);

  if (notFound) return <div className={`${CONTAINER} py-16 animate-fade-up`}><div data-testid="federation-not-found" className="text-center py-20 text-slate-500 font-mono text-sm uppercase tracking-widest border border-dashed border-[rgba(212,175,55,0.25)] rounded-xl">Federation not found</div></div>;
  if (error) return (
    <div className={`${CONTAINER} py-16 animate-fade-up`}>
      <div data-testid="federation-error" className="text-center py-20 border border-dashed border-rose-500/30 rounded-xl">
        <AlertTriangle className="w-7 h-7 text-rose-400 mx-auto mb-3" />
        <div className="text-slate-300 font-mono text-sm uppercase tracking-widest mb-4">Could not load this federation</div>
        <button onClick={load} data-testid="federation-retry" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-bold uppercase tracking-wide bg-[#161616] border border-[rgba(212,175,55,0.3)] text-slate-200 hover:border-[#E5B842]/60 hover:text-[#E5B842]" style={{ transition: "border-color 0.15s ease, color 0.15s ease" }}><RotateCw className="w-4 h-4" /> Retry</button>
      </div>
    </div>
  );
  if (!f) return (
    <div className={`${CONTAINER} py-16`} data-testid="federation-loading">
      <div className="h-5 w-32 bg-[#141414] rounded mb-6 animate-pulse" />
      <div className="h-40 rounded-2xl bg-[#0f0f0f] border border-[rgba(212,175,55,0.14)] animate-pulse" />
    </div>
  );

  const isWorld = f.level === "WORLD";
  const hasCountry = !!f.country;

  // Lineage: walk up parent_id chain from the full list (root → ... → self).
  const byId = {};
  all.forEach((x) => { byId[x.id] = x; });
  const lineage = [];
  let cursor = f.parent_id ? byId[f.parent_id] : null;
  const guard = new Set();
  while (cursor && !guard.has(cursor.id)) { guard.add(cursor.id); lineage.unshift(cursor); cursor = cursor.parent_id ? byId[cursor.parent_id] : null; }

  // Member federations (direct children).
  const children = all.filter((x) => x.parent_id === f.id);

  return (
    <div className="animate-fade-up pb-16">
      <div className={`${CONTAINER} pt-8`}>
        <Link to="/federations" data-testid="federation-back" className="inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-[#E5B842] mb-6" style={{ transition: "color 0.15s ease" }}>
          <ArrowLeft className="w-4 h-4" /> All federations
        </Link>

        {/* Governance lineage breadcrumb */}
        {(lineage.length > 0) && (
          <div data-testid="federation-lineage" className="mb-4 flex items-center gap-2 flex-wrap font-mono text-[11px]">
            <Network className="w-3.5 h-3.5 text-[#E5B842]" />
            {lineage.map((anc) => (
              <span key={anc.id} className="inline-flex items-center gap-2">
                <Link to={`/federation/${anc.wsbf_fed_id}`} className="uppercase tracking-widest text-slate-400 hover:text-[#E5B842]" style={{ transition: "color 0.15s ease" }}>{anc.short_code || anc.name}</Link>
                <ChevronRight className="w-3.5 h-3.5 text-slate-700" />
              </span>
            ))}
            <span className="uppercase tracking-widest text-[#E5B842] font-bold">{f.short_code || f.name}</span>
          </div>
        )}

        {/* ROW 1 — authority credential */}
        <div data-testid="federation-passport" className="relative overflow-hidden rounded-2xl foil-border bg-gradient-to-b from-[#161616] to-[#070707]">
          <div className="absolute inset-0 mesh-grid opacity-[0.22] pointer-events-none" />
          <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(90% 120% at 90% 0%, rgba(212,175,55,0.12) 0%, transparent 55%)" }} />
          <div className="relative p-6 sm:p-8 flex flex-col lg:flex-row lg:items-center gap-6">
            <div className="relative shrink-0 w-24 h-24 rounded-2xl bg-[#000000] border border-[#E5B842]/40 flex items-center justify-center">
              {isWorld ? <Globe className="w-12 h-12 text-[#E5B842]" /> : <Landmark className="w-12 h-12 text-[#E5B842]" />}
              {hasCountry && <span className="absolute -bottom-2 -right-2"><Flag country={f.country} width={30} /></span>}
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-mono">{LEVEL_LABEL[f.level]}</span>
                <StatusBadge status={f.recognition_status} />
              </div>
              <h1 className="font-heading text-3xl sm:text-4xl font-black uppercase tracking-tight text-slate-50 mt-1 leading-none wsbf-slant-block break-words">{f.name}</h1>
              <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-x-6 gap-y-2.5">
                <div><div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-mono mb-0.5">WSTF Fed ID</div><span className="font-mono font-bold tracking-widest text-[#E5B842]">{f.wsbf_fed_id}</span></div>
                {f.short_code && <div><div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-mono mb-0.5">Short Code</div><span className="text-slate-100 font-semibold">{f.short_code}</span></div>}
                <div><div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-mono mb-0.5">Territory</div>{hasCountry ? <span className="text-slate-100 inline-flex items-center gap-1.5"><Flag country={f.country} width={18} />{f.country.name}</span> : <span className="text-slate-100">International</span>}</div>
                <div><div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-mono mb-0.5">Recognition</div><span className="text-slate-100">{f.recognition_status}</span></div>
              </div>
            </div>
          </div>
          <div className="relative mx-6 sm:mx-8 mb-6 h-1.5 rounded-full" style={{ background: "linear-gradient(90deg,#9B781E,#F3D279,#9B781E)" }} />
        </div>

        {/* ROW 2 — governance + membership stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
          <div className={`${PANEL} p-4`} data-testid="fed-governance">
            <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-mono mb-1">Governance Level</div>
            <div className="font-heading text-lg font-black text-[#E5B842] uppercase">{f.level}</div>
            <div className="text-[11px] text-slate-500 mt-1 leading-snug">{HIERARCHY[f.level]}</div>
          </div>
          {hasCountry ? (
            <>
              <StatBox label="Member Clubs" value={f.clubs_count} Icon={Building2} />
              <StatBox label="Registered Players" value={f.players_count} Icon={Users} />
              <Link to="/rankings?entity=clubs&ranking=overall&scope=world" className={`${PANEL} p-4 group hover:border-[#E5B842]/45`} style={{ transition: "border-color 0.15s ease" }} data-testid="fed-rankings-link">
                <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-mono mb-1 flex items-center justify-between">Club Rankings <ArrowUpRight className="w-3 h-3 text-slate-600 group-hover:text-[#E5B842]" /></div>
                <div className="font-heading text-lg font-black text-slate-50 flex items-center gap-1"><TrendingUp className="w-4 h-4 text-[#E5B842]" /> View</div>
              </Link>
            </>
          ) : (
            <>
              <StatBox label={CHILD_LABEL[f.level]} value={children.length} Icon={Landmark} />
              <div className={`${PANEL} p-4 col-span-2`}>
                <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-mono mb-1">Scope</div>
                <div className="text-slate-300 text-sm">{isWorld ? "Apex governing body of the WSTF pyramid — its continental confederations are listed below." : "Continental confederation under the WSTF world body — its national member federations are listed below."}</div>
              </div>
            </>
          )}
        </div>

        {/* ROW 3a — member federations (WORLD / CONTINENTAL) */}
        {!hasCountry && (
          <div className="mt-8" data-testid="fed-children-section">
            <h2 className="font-heading text-2xl font-extrabold uppercase tracking-tight text-slate-50 mb-4 flex items-center gap-2">
              <Landmark className="w-5 h-5 text-[#E5B842]" /> {CHILD_LABEL[f.level]} <span className="text-slate-500 text-base font-mono">({children.length})</span>
            </h2>
            {children.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-4" data-testid="fed-children-grid">
                {children.map((c) => <FedMiniCard key={c.id} f={c} />)}
              </div>
            ) : (
              <div data-testid="fed-children-empty" className="text-center py-14 text-slate-500 font-mono text-sm uppercase tracking-widest border border-dashed border-[rgba(212,175,55,0.22)] rounded-xl">
                No member federations registered
              </div>
            )}
          </div>
        )}

        {/* ROW 3b — member clubs (NATIONAL) */}
        {hasCountry && (
          <div className="mt-8">
            <h2 className="font-heading text-2xl font-extrabold uppercase tracking-tight text-slate-50 mb-4 flex items-center gap-2">
              <Building2 className="w-5 h-5 text-[#E5B842]" /> Member Clubs <span className="text-slate-500 text-base font-mono">({f.clubs_count})</span>
            </h2>
            {f.clubs && f.clubs.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-4" data-testid="fed-clubs-grid">
                {f.clubs.map((c) => {
                  const nameLen = (c.name || "").length;
                  const nameSize = nameLen > 26 ? "text-sm" : "text-base";
                  return (
                  <Link key={c.id} to={`/club/${c.id}`} data-testid={`fed-club-${c.wsbf_club_id}`} className="h-full flex items-start gap-3 rounded-xl bg-[#0f0f0f] border border-[rgba(212,175,55,0.16)] p-4 hover:border-[#E5B842]/45" style={{ transition: "border-color 0.15s ease" }}>
                    <ClubCrest club={c} size={44} />
                    <div className="min-w-0 flex-1">
                      <div className={`font-heading ${nameSize} font-bold uppercase tracking-tight text-slate-50 leading-snug`}>{c.name}</div>
                      <div className="font-mono text-[11px] text-[#E5B842] mt-0.5">{c.wsbf_club_id}</div>
                    </div>
                    <StatusBadge status={c.status} />
                  </Link>
                  );
                })}
              </div>
            ) : (
              <div data-testid="fed-clubs-empty" className="text-center py-14 text-slate-500 font-mono text-sm uppercase tracking-widest border border-dashed border-[rgba(212,175,55,0.22)] rounded-xl">
                No member clubs registered
              </div>
            )}
            {f.clubs && f.clubs.length >= 50 && (
              <div className="mt-3 text-xs font-mono text-slate-500">Showing the first 50 member clubs.</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
